import { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Shared
    newSearch: 'New Search',
    pleaseProvideUsername: 'Please provide a username.',
    games: 'games',
    peak: 'peak',
    white: 'White',
    black: 'Black',
    of: 'of',
    analyzed: 'analyzed',

    // Analysis Header tabs
    tabFreeReport: 'Free Report',
    tabReport: 'Report',
    tabOpenings: 'Openings',
    tabTime: 'Time Management',
    tabStyle: 'Play Style',
    tabOpponents: 'Opponents',

    // HomePage Nav
    navFeatures: 'Features',
    navServices: 'Services',
    navHowItWorks: 'How it works',
    navPricing: 'Pricing',
    navFaq: 'FAQ',
    logIn: 'Log in',

    // Splash
    splashSubtitle: 'Your AI chess coach',

    // Hero
    heroSubtitle: 'Your AI chess coach. Analyze games,',
    heroSubtitle2: 'find weaknesses, reach your dream Elo.',
    heroInputLabel: 'Chess.com Username',
    heroButton: 'Analyze My Games \u2014 Free',
    heroNote: 'No sign-up required. Instant results.',
    scrollToExplore: 'Scroll to explore',

    // Features
    featuresTitle: 'Everything you need to improve',
    featuresSubtitle: 'Destrier analyzes your chess.com games and gives you a clear path to getting better.',
    featDeepAnalysis: 'Deep Game Analysis',
    featDeepAnalysisDesc: 'Every move reviewed. Spot blunders, missed tactics, and inaccuracies across all your games automatically.',
    featWeaknesses: 'Find Your Weaknesses',
    featWeaknessesDesc: "Destrier identifies patterns in your losses \u2014 whether it's endgames, openings, or time pressure.",
    featElo: 'Elo Tracking & Goals',
    featEloDesc: 'Set your dream Elo, track progress over time, and see which improvements will get you there fastest.',
    featAI: 'AI-Powered Coaching',
    featAIDesc: 'Get personalized tips and training plans. Destrier adapts to your skill level and playing style.',
    featOpening: 'Opening Repertoire',
    featOpeningDesc: 'Discover which openings work best for you. Build a repertoire based on your actual results.',
    featCompare: 'Beat Your Friends',
    featCompareDesc: 'Compare stats with any chess.com player. Know their weaknesses before they know yours.',

    // Stats showcase
    statsGames: 'Games Analyzed',
    statsPlayers: 'Active Players',
    statsAccuracy: 'Accuracy Rate',
    statsElo: 'Avg Elo Gain',

    // Services
    servicesTitle: 'Explore our services',
    servicesSubtitle: 'Click to learn more about what Destrier can do for your game.',
    svcAnalysisTitle: 'Game Analysis',
    svcAnalysisSummary: 'Move-by-move breakdown of every game you play.',
    svcAnalysisDetail: "Destrier scans every move for blunders, inaccuracies, and missed tactics. You get a visual timeline showing where things went right\u2014and where they didn't. Each game is rated and benchmarked against players at your level.",
    svcCoachingTitle: 'Personalized Coaching',
    svcCoachingSummary: 'AI-generated training plans tailored to your weaknesses.',
    svcCoachingDetail: "Based on your game history, Destrier creates weekly drills targeting your specific weak spots. Whether it's endgame technique, tactical vision, or time management, you'll get exercises designed just for you.",
    svcOpeningsTitle: 'Opening Explorer',
    svcOpeningsSummary: 'Discover which openings give you the best results.',
    svcOpeningsDetail: "See your win rate, accuracy, and average game length for every opening you've ever played. Destrier recommends new lines to try based on your style and suggests which openings to drop.",
    svcEloTitle: 'Elo Forecasting',
    svcEloSummary: 'See where your rating is headed with data-driven predictions.',
    svcEloDetail: 'Using your improvement trends, game frequency, and skill trajectory, Destrier projects your future Elo. Set milestones and track how close you are to breaking your next rating barrier.',
    svcRivalTitle: 'Rival Comparison',
    svcRivalSummary: 'Compare your stats head-to-head with any player.',
    svcRivalDetail: 'Enter any chess.com username and see a side-by-side breakdown. Compare openings, accuracy, time usage, and blunder rates. Know their weaknesses before your next match.',

    // How it works
    howTitle: 'How it works',
    howSubtitle: 'Three steps. Zero friction.',
    howStep1Title: 'Enter your username',
    howStep1Desc: 'Just type your chess.com username. No sign-up needed.',
    howStep2Title: 'Destrier analyzes everything',
    howStep2Desc: 'All your games are scanned for patterns, mistakes, and trends.',
    howStep3Title: 'Get your personalized report',
    howStep3Desc: 'Strengths, weaknesses, Elo predictions, and a plan to improve.',

    // Pricing
    pricingTitle: 'Simple pricing',
    pricingSubtitle: "Start free. Upgrade when you're ready to get serious.",
    pricingFree: 'Free',
    pricingPro: 'Pro',
    pricingPremium: 'Premium',
    pricingMo: '/mo',
    pricingPopular: 'POPULAR',
    pricingGetStarted: 'Get Started',
    pricingTrial7: 'Start 7-Day Free Trial',
    pricingTrial14: 'Start 14-Day Free Trial',
    freeFeat1: 'Last 50 games analyzed',
    freeFeat2: 'Basic statistics',
    freeFeat3: 'Win/loss breakdown',
    freeFeat4: 'Opening overview',
    proFeat1: 'Unlimited game analysis',
    proFeat2: 'AI coaching & suggestions',
    proFeat3: 'Weakness deep-dives',
    proFeat4: 'Elo predictions & goals',
    proFeat5: 'Opening repertoire builder',
    proFeat6: 'Compare with any player',
    premFeat1: 'Everything in Pro',
    premFeat2: 'Real-time game analysis',
    premFeat3: 'Grandmaster game comparisons',
    premFeat4: 'Custom training programs',
    premFeat5: 'Endgame tablebase access',
    premFeat6: 'Tournament preparation tools',
    premFeat7: 'Priority support',

    // FAQ
    faqTitle: 'Frequently asked questions',
    faqSubtitle: 'Everything you need to know about Destrier.',
    faq1Q: 'Is Destrier really free to use?',
    faq1A: 'Yes! The free tier lets you analyze your last 50 games with basic statistics, win/loss breakdowns, and opening overviews. No sign-up or credit card required.',
    faq2Q: 'How does Destrier analyze my games?',
    faq2A: 'Destrier connects to the chess.com public API using your username. It fetches your game history and runs each game through our AI engine to identify blunders, missed tactics, and strategic patterns.',
    faq3Q: 'Do I need to create an account?',
    faq3A: "No account needed for the free tier. Just enter your chess.com username and get instant results. You only need an account if you want to save your analysis history or upgrade to Pro or Premium.",
    faq4Q: 'Does Destrier support Lichess?',
    faq4A: "Lichess support is coming soon! Right now Destrier works exclusively with chess.com, but we're actively working on Lichess integration.",
    faq5Q: 'How accurate are the Elo predictions?',
    faq5A: 'Our Elo forecasting model uses your improvement trend, game frequency, and accuracy metrics. While no prediction is perfect, our model has shown strong correlation with actual rating changes over 3-6 month windows.',
    faq6Q: 'Can I cancel my Pro or Premium subscription anytime?',
    faq6A: "Absolutely. You can cancel your Pro or Premium subscription at any time with no questions asked. You'll keep access until the end of your billing period.",
    faq7Q: 'Is my data private and secure?',
    faq7A: "We only access publicly available game data through the chess.com API. We don't store your password or any private information. Your analysis data is encrypted and you can delete it anytime.",

    // Social
    socialFollow: 'Follow us for chess tips & updates',

    // Final CTA
    ctaTitle: 'Ready to reach your dream Elo?',
    ctaSubtitle: 'Join thousands of players who improved their game with Destrier. Start analyzing in seconds.',
    ctaPlaceholder: 'Chess.com username',
    ctaButton: 'Analyze Free',

    // Footer
    footerPrivacy: 'Privacy Policy',
    footerTerms: 'Terms of Use',
    footerCookie: 'Cookie Policy',
    footerContact: 'Contact Us',
    footerAccessibility: 'Accessibility',
    footerColorPalette: 'Color Palette',
    footerResetDefaults: 'Reset defaults',

    // Loading steps (used as keys in loadingSteps arrays)
    loadConnecting: 'Connecting to Chess.com',
    loadRetrieving: 'Retrieving game history',
    loadParsingMoves: 'Parsing moves & openings',
    loadRunningAnalysis: 'Running performance analysis',
    loadCalculatingElo: 'Calculating Elo trends',
    loadBuildingReport: 'Building your report',
    loadBuildingDashboard: 'Building your dashboard',
    loadDetectingOpenings: 'Detecting openings from moves',
    loadCalculatingWinRates: 'Calculating win rates',
    loadFindingWeaknesses: 'Finding weaknesses',
    loadBuildingRecommendations: 'Building recommendations',
    loadMappingRating: 'Mapping rating history',
    loadAnalyzingTrends: 'Analyzing trends',
    loadBuildingTrajectory: 'Building trajectory',
    loadParsingClock: 'Parsing clock data',
    loadAnalyzingTimeHabits: 'Analyzing time habits',
    loadDetectingPatterns: 'Detecting patterns',
    loadBuildingInsights: 'Building insights',
    loadAnalyzingPieces: 'Analyzing piece preferences',
    loadProfilingStyle: 'Profiling play style',
    loadMatchingLegends: 'Matching against legends',
    loadBuildingProfile: 'Building your profile',
    loadIdentifyingOpponents: 'Identifying opponents',
    loadAnalyzingMatchups: 'Analyzing matchups',

    // Loading text
    analyzingUser: 'Analyzing',
    analyzingOpenings: "'s openings",
    matchingOpeningDb: 'Matching moves against our opening database',
    analyzingRatingHistory: "'s rating history",
    mappingEloJourney: 'Mapping your Elo journey over time',
    analyzingTimeManagement: "'s time management",
    parsingClockData: 'Parsing clock data from every game',
    profilingStyle: "'s style",
    analyzingEveryMove: "Analyzing every move you've ever played",
    analyzingOpponents: "'s opponents",
    profilingEveryPlayer: "Profiling every player you've faced",
    analyzingUsername: 'Analyzing',
    thisMyTakeMoment: 'This may take a moment for large game histories',
    retrievingGames: 'Retrieving',
    gamesPlus: '+ games',
    connectingToChessCom: 'Connecting to Chess.com...',
    estimatedWait: 'Estimated wait:',
    loadingReport: 'Loading report\u2026',

    // Report page
    yourChessArchetype: 'Your Chess Archetype',
    playerSummary: 'Player Summary',
    labelGames: 'Games',
    labelWinRate: 'Win Rate',
    labelCurrentElo: 'Current Elo',
    labelPeakElo: 'Peak Elo',
    whatYoureGoodAt: "What You're Good At",
    yourStrengths: 'Your Strengths',
    peakMoments: 'Peak Moments',
    yourBestPerformances: 'Your Best Performances',
    biggestUpsetWin: 'Biggest Upset Win',
    youBeatA: 'You beat a',
    ratedPlayerWhileRated: '-rated player while rated',
    pointUpset: 'point upset',
    playingAs: 'playing as',
    inA: 'in a',
    game: 'game',
    usingThe: 'using the',
    viewGame: 'View game \u2192',
    growthAreas: 'Growth Areas',
    opportunitiesToImprove: 'Opportunities to Improve',
    growthNote: "These aren't weaknesses \u2014 they're your biggest untapped potential.",
    patternsToWatch: 'Patterns to Watch',
    whatDestrierNoticed: 'What Destrier Noticed',
    yourScore: 'Your Score',
    bobbyImprovementScore: 'Destrier Improvement Score',
    scoreHigh: "You're in strong shape. Focus on consistency and mastering your weaker formats to push even higher.",
    scoreMid: 'You have a solid foundation with clear areas for growth. Targeted practice in your weaker spots will yield fast gains.',
    scoreLow: "There's significant room for improvement \u2014 and that's exciting. Small changes to your repertoire and time management can make a big impact.",
    comeBackAfter50: 'Come back after 50 games and see how your score changes.',
    hideFullStats: 'Hide Full Stats',
    viewFullStats: 'View Full Stats',
    fullStatistics: 'Full Statistics',
    // Report - generated narrative
    strongAs: 'Strong as',
    youPerformBestWith: 'You perform best with the',
    piecesWinning: 'pieces, winning',
    ofYourGames: '% of your games.',
    killerOpening: 'Killer Opening',
    isYourMostLethal: 'is your most lethal weapon with a',
    winRatePercent: '% win rate.',
    rockSolidConsistency: 'Rock-Solid Consistency',
    ratingBarelyFluctuates: 'Your rating barely fluctuates \u2014 a sign of a disciplined, tilt-free player.',
    needsWork: 'Needs Work',
    atWinRateIn: 'At',
    winRateInFormat: '% win rate in',
    clearGapCompared: ", there's a clear gap compared to your stronger formats.",
    sideStruggles: 'Side Struggles',
    yourWinRateOf: 'Your',
    winRateLags: '% lags behind \u2014 closing this gap would significantly boost your overall results.',
    openingTrap: 'Opening Trap',
    isCostingYouGames: 'is costing you games \u2014 only',
    considerDropping: '% win rate. Consider dropping or studying it.',
    narrowRepertoire: 'Your opening repertoire is very narrow \u2014 opponents at your level may start exploiting this predictability.',
    hugeVariety: "You play a huge variety of openings. While this keeps opponents guessing, it may be spreading your preparation too thin.",
    droppedPoints: "You've dropped",
    pointsFromPeak: 'points from your peak of',
    couldIndicateDip: '. This could indicate a form dip or a shift in playing habits.',
    nearPeak: "You're playing near your all-time peak \u2014 you're in great form right now.",
    almostNeverDraw: "You almost never draw. This decisive style can be a double-edged sword \u2014 great for winning, but you might benefit from learning when to take the safe half-point.",
    highDrawRate: "Your draw rate is quite high. Consider whether some of those draws were won positions you didn't convert.",
    // Pattern keys (used in generatePatterns)
    patternNarrowRepertoire: 'Your opening repertoire is very narrow \u2014 opponents at your level may start exploiting this predictability.',
    patternWideRepertoire: "You play a huge variety of openings. While this keeps opponents guessing, it may be spreading your preparation too thin.",
    patternDroppedPre: "You've dropped ",
    patternDroppedMid: ' points from your peak of ',
    patternDroppedPost: '. This could indicate a form dip or a shift in playing habits.',
    patternNearPeak: "You're playing near your all-time peak \u2014 you're in great form right now.",
    patternRareDraws: "You almost never draw. This decisive style can be a double-edged sword \u2014 great for winning, but you might benefit from learning when to take the safe half-point.",
    patternHighDraws: "Your draw rate is quite high. Consider whether some of those draws were won positions you didn't convert.",
    // Strength/Growth text templates
    dominance: 'Dominance',
    yourWinRateOf2: 'Your',
    winRateAcross: '% win rate across',
    gamesIsStrongest: 'games is your strongest format.',
    performBestWith: 'You perform best with the',
    piecesWinning2: 'pieces, winning',
    ofYourGames2: '% of your games.',
    theOpening: 'The',
    mostLethalWeapon: 'is your most lethal weapon with a',
    winRateDot: '% win rate.',
    ratingBarelyFluctuates2: 'Your rating barely fluctuates \u2014 a sign of a disciplined, tilt-free player.',
    atWinRate: 'At',
    winRateIn: '% win rate in',
    clearGap: ", there's a clear gap compared to your stronger formats.",
    winRateLags2: '% lags behind \u2014 closing this gap would significantly boost your overall results.',
    costingGames: 'is costing you games \u2014 only',
    considerDropping2: '% win rate. Consider dropping or studying it.',
    // Summary text
    withGames: 'With',
    gamesUnderBelt: 'games under your belt',
    andPeakRating: 'and a peak rating of',
    investedTime: ", you've clearly invested serious time into your chess.",
    strongestFormat: 'Your strongest format is',
    whereDominate: ', where you dominate with a',
    asWhiteGravitate: 'As white, you gravitate toward the',
    asBlackRely: ', and as black you rely on the',

    // Stats page
    yourDashboard: 'Your Dashboard',
    performanceBreakdown: 'Performance breakdown across all game types',
    noStatsFound: 'No stats found for this player.',
    totalGames: 'Total Games',
    winRate: 'Win Rate',
    wins: 'wins',
    mostPlayed: 'Most Played',
    byGameType: 'By Game Type',
    deepDive: 'Deep Dive',
    overview: 'Overview',
    gameTypes: 'Game Types',
    topOpenings: 'Top Openings',
    eloRanges: 'Elo Ranges',
    ratingSpread: 'Rating spread across game types',
    colorPerformance: 'Color Performance',
    estimatedWinRate: 'Estimated win rate playing as white vs black',
    gameDistribution: 'Game Distribution',
    howGamesSplit: 'How your games are split across types',
    openingRepertoire: 'Opening Repertoire',
    mostPlayedOpenings: 'Most played openings by color across all game types',
    asWhite: 'As White',
    asBlack: 'As Black',
    peakElo: 'Peak Elo',

    // Openings page
    openingsTitle: 'Opening Repertoire',
    openingsSubtitle: 'Your opening performance breakdown',
    uniqueOpenings: 'unique openings',
    diversityScore: 'Diversity Score',
    explorer: 'Explorer',
    balanced: 'Balanced',
    specialist: 'Specialist',
    bestOpening: 'Best Opening',
    worstOpening: 'Worst Opening',
    openingPerformance: 'Opening Performance',
    gamesLabel: 'games',
    winRateLabel: 'win rate',
    gamesAnalyzedDb: "games analyzed using Destrier's opening database",
    yourOpenings: 'Your Openings',
    noOpeningsFound: 'No openings found.',
    yourWeapons: 'Your Weapons',
    bestPerformingOpenings: 'Best Performing Openings',
    needsAttention: 'Needs Attention',
    underperformingOpenings: 'Underperforming Openings',
    bobbysAdvice: "Destrier's Advice",
    openingRecommendations: 'Opening Recommendations',
    openingRecommendationsDesc: 'Based on your results, consider swapping these underperforming openings for alternatives you already know.',
    loadingOpenings: 'Loading openings\u2026',

    // Elo page
    eloTrajectory: 'Elo Trajectory',
    ratingJourney: 'Your rating journey over time',
    current: 'Current',
    lowest: 'Lowest',
    netChange: 'Net Change',
    ratingOverTime: 'Rating Over Time',
    gamesTracked: 'games tracked across all time controls',
    loadingTrajectory: 'Loading trajectory\u2026',
    byTimeControl: 'By Time Control',
    ratingBreakdown: 'Rating Breakdown',

    // Time Management page
    timeManagement: 'Time Management',
    timeSubtitle: 'How you spend your clock',
    timeTroubleRate: 'Time Trouble Rate',
    fastMoves: 'Fast Moves (<2s)',
    slowMoves: 'Slow Moves (>30s)',
    avgTimeInWins: 'Avg Time in Wins',
    moves: 'moves',
    avgTimePerMove: 'Average Time Per Move',
    move: 'Move',
    seconds: 'Seconds',
    timeByPhase: 'Time by Game Phase',
    opening: 'Opening',
    middlegame: 'Middlegame',
    endgame: 'Endgame',
    timeTrouble: 'Time Trouble',
    lossesInTimeTrouble: 'Losses while in time trouble',
    timeTroubleHigh: 'You frequently run low on time. Consider playing faster in the opening to save clock for critical moments.',
    timeTroubleMid: 'You occasionally enter time trouble. Monitor your clock during the middlegame.',
    timeTroubleLow: 'Great clock discipline. You rarely find yourself in time pressure.',
    movesAnalyzedAcross: 'moves analyzed across',
    gamesUnder30s: 'games under 30s',
    vsInLosses: 'in losses',
    thinkTimeByMoveNumber: 'Think Time by Move Number',
    avgSecondsPerMove: 'Average seconds spent per move across all games',
    byGamePhase: 'By Game Phase',
    avgThinkTime: 'Average Think Time',
    clockPressure: 'Clock Pressure',
    timeTroubleAnalysis: 'Time Trouble Analysis',
    gamesEnteringTimeTrouble: 'Games entering time trouble (<30s)',
    ofTroubleGames: 'of trouble games',
    byTimeControlTime: 'By Time Control',
    avgThinkTimePerFormat: 'Average Think Time per Format',
    sAvg: 's avg',
    loadingTimeData: 'Loading time data\u2026',

    // Play Style page
    playStyle: 'Play Style',
    styleSubtitle: 'Your unique chess personality',
    styleProfile: 'Style Profile',
    kingside: 'Kingside',
    queenside: 'Queenside',
    avgGameLength: 'Avg Game Length',
    totalMovesPlayed: 'Total Moves Played',
    knightVsBishop: 'Knight vs Bishop',
    knightPlayer: 'Knight player',
    bishopPlayer: 'Bishop player',
    aggressionIndex: 'Aggression Index',
    youPlayMostLike: 'You play most like',
    matchPercent: '% match',
    favoritePiece: 'Favorite Piece',
    castling: 'Castling',
    youCastleIn: 'You castle in',
    ofYourGamesPercent: '% of your games',
    moveDistribution: 'Move Distribution',
    pieceUsage: 'Piece Usage',
    legendComparison: 'Legend Comparison',
    whichLegends: 'Which Legends Do You Play Like?',
    loadingPlayStyle: 'Loading play style\u2026',

    // Opponents page
    opponents: 'Opponents',
    opponentsSubtitle: 'Your matchup tendencies',
    giantKiller: 'Giant Killer',
    underPressure: 'Under Pressure',
    opponentPressureHigh: 'You lose to lower-rated opponents more often than expected. Stay focused against weaker players.',
    opponentPressureMid: 'Occasional slips against weaker opponents, but generally solid.',
    opponentPressureLow: 'Excellent consistency \u2014 you rarely drop games to weaker opponents.',
    vsHigherRated: 'vs Higher Rated',
    vsSimilar: 'vs Similar',
    vsLowerRated: 'vs Lower Rated',
    bestUpsetElo: 'Best:',
    winRateByOpponentRating: 'Win Rate by Opponent Rating',
    howYouPerform: 'How you perform across different Elo brackets',
    winsVsHigher: 'Wins vs opponents 100+ Elo higher',
    winsIn: 'wins in',
    biggestUpsetBeat: 'Biggest upset: beat an opponent',
    eloAboveYou: 'Elo above you',
    lossesVsLower: 'Losses vs opponents 100+ Elo lower',
    lossesIn: 'losses in',
    asColor: 'As',
    rivalries: 'Rivalries',
    mostFacedOpponents: 'Most Faced Opponents',
    loadingOpponents: 'Loading opponents\u2026',
    gamesAnalyzed: 'games analyzed',

    // Game Viewer
    gameViewer: 'Game Viewer',
    backToReport: 'Back',
    flipBoard: 'Flip Board',
    autoplay: 'Autoplay',
    stop: 'Stop',
    whiteWins: 'White wins',
    blackWins: 'Black wins',
    draw: 'Draw',
    start: 'Start',
    back: 'Back',
    forward: 'Forward',
    end: 'End',
    movesPanel: 'Moves',
    startingPosition: 'Starting position',
    finalPosition: 'Final position',
    gameDetails: 'Game Details',
    labelOpening: 'Opening',
    labelDate: 'Date',
    labelResult: 'Result',
    labelTotalMoves: 'Total Moves',
    keyboardShortcuts: 'Keyboard Shortcuts',
    prevNextMove: 'Previous / Next move',
    firstLastMove: 'First / Last move',
    arrowKeysHint: 'Use arrow keys to navigate | F to flip',

    // Theme toggle
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
  },
};

const LangContext = createContext({ lang: 'en', setLang: () => {}, t: translations.en });

export function LangProvider({ children }) {
  const [lang, setLang] = useState('en');
  const t = translations[lang] || translations.en;
  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
